import type { z } from 'zod';
import {
  hotelPhotoAlt,
  hotelPhotoUrl,
  ratingOrUndefined,
  type HotelOfferView,
  type HotelRatingView,
  type HotelRoomFilter,
  type HotelRateView,
  type HotelRoomReceiptView,
  type HotelRoomsCopy,
  type HotelRoomsContextView,
  type HotelRoomView,
} from '@voll-intelligence/intelligence-ui-kit';

import {
  htmlToPlainText,
  localizedBoardLabel,
  type ChatUiHotelAmenity,
  type HotelPolicyLimitsSchema,
  roomIdentityLabel,
  isOtherRoomsGroup,
  perNightOf,
  rankRates,
  type ChatUiHotelRoomsGroup,
  type ChatUiHotelRoomsRate,
  type HotelRoomsBlock,
} from '@voll-intelligence/genui-contract';
import type { GenUiAction } from '../../shared/ui/action.js';
import { hotelChannelBrand } from './channel_brand.js';
import { fmtDayMonth, fmtMoney, localDayIso } from '../../shared/ui/flight_format.js';
import { genUiT, resolveGenUiLocale } from '../../shared/ui/i18n.js';
import { hotelPolicyCeiling, hotelPolicyReasons } from './hotel_policy_reasons.js';
import { hotelOfferView } from './hotel_offer_view.js';

type PolicyLimits = z.infer<typeof HotelPolicyLimitsSchema>;

type HotelRoomSelectAction = Extract<GenUiAction, { type: 'hotel-room-select' }>;

type HotelRoomsAdapterSharedView = {
  rooms: HotelRoomView[];
  /** O porão: grupos `unclassified` (nome ilegível/placeholder) sem tarifa
   *  negociada (`isOtherRoomsGroup`), fora da lista principal. Ausente quando
   *  não há nenhum — nunca um array vazio. Some no recibo (ver
   *  `classifiedGroups`/`otherGroups` em `createHotelRoomsView`). */
  otherRooms?: HotelRoomView[];
  /** O card do hotel escolhido, no topo do turno. Ausente em bloco antigo (sem
   *  `offer`) — aí o card de quartos abre pelo título, como sempre abriu. */
  selectedHotel?: HotelOfferView;
  copy: HotelRoomsCopy;
  context: HotelRoomsContextView;
  selectedRateId?: string;
  /** Os chips que o cartão abre ligados (`block.rateFilters`). */
  initialFilters?: HotelRoomFilter[];
  readOnly: boolean;
};

export type HotelRoomsAdapterView =
  | (HotelRoomsAdapterSharedView & { display: 'selection'; receipt?: never })
  | (HotelRoomsAdapterSharedView & { display: 'receipt'; receipt: HotelRoomReceiptView });

type HotelRoomsViewOptions = {
  locale?: string;
  proxyImageUrl?: (url: string) => string;
};

const roomId = (group: ChatUiHotelRoomsGroup): string =>
  `room-${String(group.rates[0]?.roomIndex)}`;
const rateId = (rate: ChatUiHotelRoomsRate): string => `rate-${String(rate.roomIndex)}`;

export function createHotelRoomsCopy(locale: string): HotelRoomsCopy {
  const t = (key: Parameters<typeof genUiT>[1]) => genUiT(locale, key);
  return {
    title: t('hotel.roomsTitle'),
    negotiatedLabel: t('hotelRooms.negotiated'),
    withinPolicyFilterLabel: t('hotel.inPolicy'),
    breakfastFilterLabel: t('hotel.amenity.breakfast'),
    refundableFilterLabel: t('hotelRooms.refundable'),
    previousFilterLabel: t('hotelRooms.previousFilters'),
    nextFilterLabel: t('hotelRooms.moreFilters'),
    noFilteredRoomsLabel: t('hotelDetails.noFilteredRooms'),
    noFilteredRoomsHint: t('hotelDetails.noFilteredRoomsHint'),
    clearFiltersLabel: t('hotelDetails.clearFilters'),
    allRoomsMeetFilterLabel: (filter) =>
      genUiT(locale, 'hotelDetails.allRoomsMeetFilter', { filter }),
    roomDetailsLabel: t('hotelRooms.viewDetails'),
    roomAmenitiesLabel: t('hotelRooms.roomAmenities'),
    previousPhotoLabel: t('hotel.photoPrev'),
    nextPhotoLabel: t('hotel.photoNext'),
    selectRateLabel: t('hotelRooms.select'),
    selectedRateLabel: t('hotelRooms.selected'),
    selectedNotePrefix: t('hotelRooms.selectedPrefix'),
    showBenefitsLabel: t('hotelRooms.showAllRates'),
    otherRatesLabel: t('hotelRooms.otherRates'),
    ratesAriaLabel: t('hotelRooms.ratesAria'),
    seeMoreRatesLabel: (count) => genUiT(locale, 'hotelRooms.seeMoreRates', { count }),
    seeLessRatesLabel: t('hotelRooms.seeLessRates'),
    scarcityLabel: (count) =>
      genUiT(locale, count === 1 ? 'hotelRooms.lastUnits_one' : 'hotelRooms.lastUnits_other', {
        count,
      }),
    outOfPolicyLabel: t('hotel.outPolicy'),
    outOfPolicyHint: t('hotelRooms.outOfPolicy'),
    noRatesLabel: t('hotel.priceUnavailable'),
    closeLabel: t('common.close'),
    rateChipLabels: {
      WITH_BREAKFAST: t('hotelRooms.chipBreakfast'),
      WITHOUT_BREAKFAST: t('hotelRooms.chipNoBreakfast'),
      WITH_REFUND: t('hotelRooms.chipRefundable'),
      WITHOUT_REFUND: t('hotelRooms.chipNonRefundable'),
    },
    rateTypeLabels: {
      BREAKFAST_REFUNDABLE: t('hotelRooms.rateTypeBreakfastRefundable'),
      BREAKFAST: t('hotelRooms.rateTypeBreakfast'),
      REFUNDABLE: t('hotelRooms.rateTypeRefundable'),
      ROOM_ONLY: t('hotelRooms.rateTypeRoomOnly'),
      UNSPECIFIED: t('hotelRooms.rateTypeOther'),
    },
    otherOptionsLabel: (count) => genUiT(locale, 'hotelRooms.otherOptions', { count }),
  };
}

function ratePolicy(
  rate: ChatUiHotelRoomsRate,
  locale: string,
  limits: PolicyLimits | undefined,
): Pick<HotelRateView, 'policyLabel' | 'policyTone' | 'policyReasons'> {
  if (rate.policyStatus === 'within_policy') {
    return { policyLabel: genUiT(locale, 'hotel.inPolicy'), policyTone: 'within' };
  }
  if (rate.policyStatus === 'out_of_policy') {
    // O rótulo fica genérico (é o chip da linha da tarifa); o motivo concreto
    // vai à parte, e só quando o bloco o carrega — bloco antigo segue como era.
    const policyReasons = hotelPolicyReasons(rate.policyViolations, limits, locale);
    return {
      policyLabel: genUiT(locale, 'hotel.outPolicy'),
      policyTone: 'outside',
      ...(policyReasons.length > 0 ? { policyReasons } : {}),
    };
  }
  return { policyTone: 'unknown' };
}

function ratePricePresentation(
  rate: ChatUiHotelRoomsRate,
  nights: number | undefined,
  locale: string,
): Pick<
  HotelRateView,
  'nightlyLabel' | 'nightlyPriceLabel' | 'nightlyUnitLabel' | 'stayTotalLabel'
> {
  const nightly = perNightOf(rate, nights);
  const nightlyUnitLabel = genUiT(locale, 'hotelRooms.perNight');
  const stayTotalLabel =
    nights && rate.total
      ? `${genUiT(locale, nights === 1 ? 'hotelRooms.nights_one' : 'hotelRooms.nights_other', {
          count: nights,
        })} · ${fmtMoney(rate.total)}`
      : undefined;
  return {
    ...(nightly
      ? {
          nightlyLabel: `${fmtMoney(nightly)} ${nightlyUnitLabel}`,
          nightlyPriceLabel: fmtMoney(nightly),
          nightlyUnitLabel,
        }
      : {}),
    ...(stayTotalLabel ? { stayTotalLabel } : {}),
  };
}

/**
 * O que a tarifa entrega, virado em enum para o kit.
 *
 * O texto de regime do fornecedor (`boardDescription`: "Apenas quarto", "Café da
 * manhã continental", "Tudo incluído") NÃO entra aqui — ele diz mais que
 * `breakfast`, mas dizia em palavras diferentes a cada tarifa, e o chip com
 * rótulo variável e cor do booleano lia como dois estados ("Apenas quarto" em
 * vermelho ao lado de "Café da manhã continental" em verde). O texto segue
 * inteiro em `planLabel` (título da linha da tarifa); o chip é só o par
 * fechado.
 */
function rateBenefitPresentation(
  rate: ChatUiHotelRoomsRate,
): Pick<HotelRateView, 'breakfast' | 'refund'> {
  return {
    ...(rate.breakfast === undefined
      ? {}
      : {
          breakfast: rate.breakfast ? ('WITH_BREAKFAST' as const) : ('WITHOUT_BREAKFAST' as const),
        }),
    ...(rate.refundable === undefined
      ? {}
      : { refund: rate.refundable ? ('WITH_REFUND' as const) : ('WITHOUT_REFUND' as const) }),
  };
}

/** O canal da linha da tarifa: só a marca de consumidor (#6895). Qualquer
 *  outro fornecedor fica sem canal (#7160) — nem o nome cru do consolidador,
 *  nem um coringa "Tarifa VOLL" que rotulava a lista inteira. A negociada
 *  ganha o selo "Tarifa negociada" no card; na comparação, a linha sem canal
 *  cai no regime. */
function rateChannelLabel(rate: ChatUiHotelRoomsRate): string | undefined {
  return hotelChannelBrand(rate.supplier?.name);
}

function rateSupplierPresentation(
  rate: ChatUiHotelRoomsRate,
  locale: string,
  proxyImageUrl: (url: string) => string,
): Pick<HotelRateView, 'channelLabel' | 'channelImageUrl' | 'planLabel'> {
  const channelLabel = rateChannelLabel(rate);
  // O logo acompanha o rótulo: sem marca, o logo do consolidador o nomearia.
  const channelImage = channelLabel ? (rate.supplier?.image ?? rate.supplier?.icon) : undefined;
  return {
    ...(channelLabel ? { channelLabel } : {}),
    ...(channelImage ? { channelImageUrl: proxyImageUrl(channelImage) } : {}),
    ...(rate.boardDescription
      ? { planLabel: localizedBoardLabel(rate.boardDescription, locale) }
      : {}),
  };
}

function rateView(
  rate: ChatUiHotelRoomsRate,
  nights: number | undefined,
  locale: string,
  proxyImageUrl: (url: string) => string,
  /** O nome recomposto do QUARTO (já resolvido por `roomDisplayName`) — o
   *  fornecedor cru só imprime quando difere dele; igual seria eco puro do
   *  título que o cartão já mostra. */
  roomName: string,
): Omit<HotelRateView, 'policyLabel' | 'policyTone' | 'policyReasons'> {
  return {
    id: rateId(rate),
    ...rateSupplierPresentation(rate, locale, proxyImageUrl),
    ...(rate.sourceName && rate.sourceName !== roomName ? { sourceName: rate.sourceName } : {}),
    totalLabel: rate.total ? fmtMoney(rate.total) : genUiT(locale, 'hotel.priceUnavailable'),
    ...(rate.total ? { total: rate.total } : {}),
    ...ratePricePresentation(rate, nights, locale),
    // #3287: `freeCancellationUntil` é o `from` da primeira multa — um prazo
    // que já passou é uma tarifa que já NÃO cancela grátis, e anunciá-lo
    // ("Cancela grátis até 15 set" em 18/09) é mentir sobre dinheiro. Só com o
    // prazo no FUTURO, no dia LOCAL do viajante (`localDayIso`): no próprio dia
    // a multa já começou (é o `from` dela — mesma régua do agente).
    ...(rate.freeCancellationUntil && rate.freeCancellationUntil > localDayIso()
      ? {
          cancellationLabel: genUiT(locale, 'hotelRooms.freeCancelUntil', {
            date: fmtDayMonth(rate.freeCancellationUntil, locale),
          }),
        }
      : {}),
    ...rateBenefitPresentation(rate),
    ...(rate.negotiated === undefined ? {} : { negotiated: rate.negotiated }),
    // `rate.quantity` NÃO vira `remainingRooms`: no contrato do provedor ele é a
    // QUANTIDADE DE QUARTOS COTADOS (confirmado 2026-08-10), não estoque restante —
    // projetá-lo acendia "Último 1" em toda tarifa de busca de 1 quarto, urgência
    // fabricada. O campo segue no bloco (replay/fato); o selo de escassez do kit
    // fica adormecido até existir um campo de estoque de verdade.
    available: true,
  };
}

function stayLabel(block: HotelRoomsBlock, locale: string): string | undefined {
  const parts: string[] = [];
  if (block.checkin && block.checkout && block.nights) {
    parts.push(
      genUiT(locale, 'hotelRooms.headerStay', {
        checkin: fmtDayMonth(block.checkin, locale, []),
        checkout: fmtDayMonth(block.checkout, locale, [block.checkin, block.checkout]),
        nights: genUiT(locale, block.nights === 1 ? 'hotel.night_one' : 'hotel.night_other', {
          count: block.nights,
        }),
      }),
    );
  }
  // `adultsCount` é o nome legado do mesmo dado — bloco persistido antes de
  // 2026-08-07 ainda chega com ele (ver `hotel_rooms_schema.ts`).
  const guestsCount = block.guestsCount ?? block.adultsCount;
  if (block.roomsCount && guestsCount) {
    parts.push(
      genUiT(locale, 'hotelRooms.headerOccupancy', {
        rooms: genUiT(
          locale,
          block.roomsCount === 1 ? 'hotelPlanner.roomCount_one' : 'hotelPlanner.roomCount_other',
          { count: block.roomsCount },
        ),
        guests: genUiT(
          locale,
          guestsCount === 1 ? 'hotelPlanner.guestCount_one' : 'hotelPlanner.guestCount_other',
          { count: guestsCount },
        ),
      }),
    );
  }
  return parts.join(' · ') || undefined;
}

function hotelRating(
  rating: number | undefined,
  reviewCount: number | undefined,
  locale: string,
): HotelRatingView | undefined {
  const shownRating = ratingOrUndefined(rating);
  if (shownRating === undefined) return undefined;
  const resolvedLocale = resolveGenUiLocale(locale);
  const shown = shownRating.toLocaleString(resolvedLocale, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
  return {
    /* A frase acessível carrega a contagem por extenso — o leitor de tela não
     *  pode anunciar "(3.218)" sem substantivo (CodeRabbit, PR #1806). */
    label:
      reviewCount === undefined
        ? genUiT(locale, 'hotel.rating', { rating: shown })
        : `${genUiT(locale, 'hotel.rating', { rating: shown })} · ${genUiT(
            locale,
            reviewCount === 1 ? 'hotelRooms.reviews_one' : 'hotelRooms.reviews_other',
            { count: reviewCount },
          )}`,
    /* O número que a superfície mostra ao lado da ESTRELA — a frase acessível
     *  continua inteira em `label`. */
    valueLabel: shown,
    /* A contagem entre parênteses é o lastro da nota (protótipo do design hub):
     *  pontuação numérica, igual nas três línguas — por isso sem chave de i18n;
     *  a frase por extenso segue em `hotelRooms.reviews_*` para o detalhe. */
    ...(reviewCount === undefined
      ? {}
      : { reviewsLabel: `(${reviewCount.toLocaleString(resolvedLocale)})` }),
  };
}

function roomsContext(block: HotelRoomsBlock, locale: string): HotelRoomsContextView {
  const rating = hotelRating(block.hotel.rating, block.hotel.reviewCount, locale);
  const stay = stayLabel(block, locale);
  const policyCeiling = hotelPolicyCeiling(block.offer?.policyLimits, locale);
  return {
    hotelName: block.hotel.title,
    ...(rating ? { rating } : {}),
    ...(stay ? { stayLabel: stay } : {}),
    ...(policyCeiling ? { policyCeiling } : {}),
  };
}

function receiptView(block: HotelRoomsBlock, locale: string): HotelRoomReceiptView {
  const stayLine = stayLabel(block, locale);
  return {
    hotelName: block.hotel.title,
    ...(stayLine ? { stayLine } : {}),
  };
}

/** As tarifas do quarto por preço, a mais barata na frente (#6914). A ordem
 *  INTEIRA conta (#6470): o kit mostra a primeira que passa nos chips ligados,
 *  e só assim ela é a mais barata do recorte — a mesma que o destaque do
 *  agente escolhe (`roomsFocusFor`). */
function orderedRates(group: ChatUiHotelRoomsGroup): ChatUiHotelRoomsRate[] {
  return rankRates(group.rates);
}

/** Comodidade com `code` sai traduzida; sem `code`, o texto do fornecedor. Ver
 *  `shared/hotel_amenity_schema.ts`. */
function amenityLabels(amenities: readonly ChatUiHotelAmenity[], locale: string): string[] {
  return amenities.map((amenity) =>
    amenity.code ? genUiT(locale, `hotel.amenity.${amenity.code}`) : amenity.label,
  );
}

/**
 * Separa os labels do quarto em área, capacidade e cama para a linha de fatos
 * (régua / pessoas / cama), pelo texto — não há id de catálogo aqui como há na
 * comodidade do hotel (`shared/hotel_amenity_schema.ts`).
 *
 * TODO(hotel): trocar por fonte tipada. Isto é inferência de texto, com os
 * mesmos limites de sempre: os padrões são PT/EN, então "2 personas" ou "25 sq
 * ft" perdem o ícone e caem na lista genérica. Vive porque hoje não há
 * alternativa, e por ora nem entrada: `SearchHotelRoomDetail` devolveu
 * `amenities: []` na amostra de produção (quarto COM `mediaRoomId`), e no
 * develop a query nem é chamada porque nenhuma unidade traz `mediaRoomId`.
 *
 * Os fatos existem, mas em PROSA e nos dois campos que o adapter descarta hoje:
 * `description` do detalhe do quarto ("28m² apartments with one bedroom... a
 * king-size bed or two single beds") e `moreInformation` da unidade — este
 * último com HTML e o bloco de políticas colado. O caminho certo é o fornecedor
 * tipar área/cama/ocupação, ou nós extrairmos disso UMA vez na raiz
 * (modal-session) em vez de na tela.
 */
function roomPresentation(
  labels: readonly string[],
): Pick<HotelRoomView, 'amenities' | 'areaLabel' | 'capacityLabel' | 'bedLabel' | 'description'> {
  let areaLabel: string | undefined;
  let capacityLabel: string | undefined;
  let bedLabel: string | undefined;
  const amenities: string[] = [];

  labels.forEach((label) => {
    /* Um dígito basta: todo número termina num dígito antes do "m²"/"m2", e o
     * `\d+(?:[.,]\d+)?` de antes tinha backtracking super-linear (S5852). */
    if (!areaLabel && /\d\s*m[²2]/i.test(label)) {
      areaLabel = label;
      return;
    }
    if (!capacityLabel && /hóspede|hospede|pessoa|guest|adult/i.test(label)) {
      capacityLabel = label;
      return;
    }
    if (!bedLabel && /cama|bed|king|queen|twin|sofá-cama|sofa-cama/i.test(label)) {
      bedLabel = label;
      return;
    }
    amenities.push(label);
  });

  return {
    amenities,
    ...(amenities.length > 0 ? { description: amenities.join(' · ') } : {}),
    ...(areaLabel ? { areaLabel } : {}),
    ...(capacityLabel ? { capacityLabel } : {}),
    ...(bedLabel ? { bedLabel } : {}),
  };
}

/**
 * Nome do quarto pro locale pedido — a MESMA regra em toda superfície que
 * nomeia um grupo (o card e a mensagem ecoada na seleção), pra elas nunca
 * divergirem de novo (achado da revisão da Task 7: `resolveHotelRoomSelection`
 * ecoava `group.name`, gravado fixo em pt-BR pelo builder, enquanto o card já
 * mostrava o nome recomposto pelo locale da sessão). Recomposto pelos TOKENS
 * de `identity` — só cai no nome cru do fornecedor quando o builder não
 * classificou o grupo (`unclassified`, sem `identity`). Ver `roomIdentityLabel`.
 *
 * #5922: o grupo ilegível que a tarifa negociada tira do porão (o fornecedor
 * nomeia o quarto do acordo só com o nome da empresa) ganha um título que diz
 * o que ele é; o nome cru deixa de ser eco do título e passa a sair como
 * `sourceName` na linha da tarifa e `roomSource` no checkout. Bloco sem
 * `identity` e sem `unclassified` (anterior à taxonomia) segue com o nome
 * gravado: ali o nome é legível.
 */
function roomDisplayName(group: ChatUiHotelRoomsGroup, locale: string): string {
  if (group.identity) return roomIdentityLabel(group.identity, locale);
  if (group.unclassified && !isOtherRoomsGroup(group)) {
    return genUiT(locale, 'hotelRooms.negotiatedRoom');
  }
  return group.name;
}

function roomView(
  group: ChatUiHotelRoomsGroup,
  block: HotelRoomsBlock,
  locale: string,
  proxyImageUrl: (url: string) => string,
): HotelRoomView {
  const id = roomId(group);
  const name = roomDisplayName(group, locale);
  /* O `about` chega cru do fornecedor, tanto no turno novo quanto no bloco já
   * persistido (#2758): a leitura é a ÚNICA camada que converte — a origem
   * grava a prosa crua justamente para a conversão não rodar duas vezes. A tela
   * recebe texto puro, nunca marcação. */
  const about = htmlToPlainText(group.about);
  return {
    id,
    name,
    ...(about ? { about } : {}),
    ...roomPresentation(amenityLabels(group.amenities ?? [], locale)),
    photos: (group.photos ?? []).map((photo, index) => ({
      id: `${id}-photo-${String(index + 1)}`,
      url: proxyImageUrl(hotelPhotoUrl(photo.url)),
      alt: hotelPhotoAlt(photo.description, name),
    })),
    rates: orderedRates(group).map((rate) => ({
      ...rateView(rate, block.nights, locale, proxyImageUrl, name),
      ...ratePolicy(rate, locale, block.offer?.policyLimits),
    })),
  };
}

/**
 * Grupos elegíveis pro turno, já divididos entre a lista principal e o porão
 * (`isOtherRoomsGroup`: nome ilegível SEM tarifa negociada — a de acordo fica
 * na lista principal, #5922). No recibo não há split: o grupo selecionado é o
 * único candidato e aparece em `classifiedGroups` do mesmo jeito, ilegível ou
 * não — é o quarto que a pessoa reservou, não um card pra escolher.
 */
function partitionRoomGroups(
  block: HotelRoomsBlock,
  receipt: boolean,
): { classifiedGroups: ChatUiHotelRoomsGroup[]; otherGroups: ChatUiHotelRoomsGroup[] } {
  const groups = receipt
    ? block.rooms.filter((group) =>
        group.rates.some((rate) => rate.roomIndex === block.selectedRateRoomIndex),
      )
    : block.rooms;
  return {
    classifiedGroups: receipt ? groups : groups.filter((group) => !isOtherRoomsGroup(group)),
    otherGroups: receipt ? [] : groups.filter(isOtherRoomsGroup),
  };
}

/** `block.rateFilters` no vocabulário dos chips; o recibo não tem chips. */
function initialFiltersOf(
  block: HotelRoomsBlock,
  receipt: boolean,
): { initialFilters?: HotelRoomFilter[] } {
  const on = block.rateFilters ?? {};
  const filters = (['policy', 'breakfast', 'refundable'] as const).filter((key) => on[key]);
  return !receipt && filters.length > 0 ? { initialFilters: filters } : {};
}

export function createHotelRoomsView(
  block: HotelRoomsBlock,
  options: HotelRoomsViewOptions = {},
): HotelRoomsAdapterView {
  const locale = options.locale ?? 'pt-BR';
  const proxyImageUrl = options.proxyImageUrl ?? ((url: string) => url);
  const selectedRate = block.rooms
    .flatMap((group) => group.rates)
    .find((rate) => rate.roomIndex === block.selectedRateRoomIndex);
  const selectedRateId = selectedRate ? rateId(selectedRate) : undefined;
  const receipt = block.viewOnly === true && selectedRateId !== undefined;
  const { classifiedGroups, otherGroups } = partitionRoomGroups(block, receipt);
  // No recibo o cabeçalho já nomeia o hotel — repetir o card ali seria eco.
  const selectedHotel =
    block.offer && !receipt
      ? hotelOfferView(block.offer, {
          locale,
          proxyImageUrl,
          // O card deste turno é leitura: quem escolhe agora é o quarto, abaixo.
          detailsAvailable: false,
          ...(block.nights === undefined ? {} : { nights: block.nights }),
        })
      : undefined;
  const sharedView: HotelRoomsAdapterSharedView = {
    rooms: classifiedGroups.map((group) => roomView(group, block, locale, proxyImageUrl)),
    copy: createHotelRoomsCopy(locale),
    context: roomsContext(block, locale),
    ...(selectedHotel ? { selectedHotel } : {}),
    ...(selectedRateId ? { selectedRateId } : {}),
    ...initialFiltersOf(block, receipt),
    ...(otherGroups.length > 0
      ? { otherRooms: otherGroups.map((group) => roomView(group, block, locale, proxyImageUrl)) }
      : {}),
    readOnly: block.viewOnly === true,
  };
  return receipt
    ? { ...sharedView, display: 'receipt', receipt: receiptView(block, locale) }
    : { ...sharedView, display: 'selection' };
}

export function resolveHotelRoomSelection(
  block: HotelRoomsBlock,
  selectedRoomId: string,
  selectedRateId: string,
  locale = 'pt-BR',
): HotelRoomSelectAction | undefined {
  const group = block.rooms.find((candidate) => roomId(candidate) === selectedRoomId);
  const rate = group?.rates.find((candidate) => rateId(candidate) === selectedRateId);
  if (!group || !rate) return undefined;
  const rateLabel =
    localizedBoardLabel(rate.boardDescription, locale) ||
    rateChannelLabel(rate) ||
    (rate.negotiated && rate.supplier?.name ? genUiT(locale, 'hotelRooms.negotiated') : undefined);
  const room = roomDisplayName(group, locale);
  return {
    type: 'hotel-room-select',
    offerId: block.hotel.offerId,
    roomIndex: rate.roomIndex,
    ...(rate.total ? { price: rate.total } : {}),
    text: rateLabel
      ? genUiT(locale, 'hotelRooms.selectText', {
          room,
          rate: rateLabel,
          hotel: block.hotel.title,
        })
      : genUiT(locale, 'hotelRooms.selectTextNoRate', {
          room,
          hotel: block.hotel.title,
        }),
  };
}

/** O detalhe EXIBÍVEL da tarifa escolhida — o que o checkout mostra para a
 *  pessoa auditar a escolha (nome canônico + nome cru do fornecedor + regime).
 *  Espelha os campos opcionais de `HotelItemDetailSchema`; separado da ação
 *  `hotel-room-select` de propósito: a ação é wire de conversa e não pode
 *  engordar por causa do funil determinístico. */
export interface HotelRateSelectionDetail {
  readonly roomName?: string;
  readonly roomSource?: string;
  readonly board?: string;
  readonly breakfast?: boolean;
  readonly refundable?: boolean;
}

export function resolveHotelRateDetail(
  block: HotelRoomsBlock,
  selectedRoomId: string,
  selectedRateId: string,
  locale = 'pt-BR',
): HotelRateSelectionDetail | undefined {
  const group = block.rooms.find((candidate) => roomId(candidate) === selectedRoomId);
  const rate = group?.rates.find((candidate) => rateId(candidate) === selectedRateId);
  if (!group || !rate) return undefined;
  const roomName = roomDisplayName(group, locale);
  return {
    roomName,
    ...(rate.sourceName && rate.sourceName !== roomName ? { roomSource: rate.sourceName } : {}),
    ...(rate.boardDescription ? { board: localizedBoardLabel(rate.boardDescription, locale) } : {}),
    ...(rate.breakfast !== undefined ? { breakfast: rate.breakfast } : {}),
    ...(rate.refundable !== undefined ? { refundable: rate.refundable } : {}),
  };
}
